/**
 * Beginner Mode: what a war is (over the war in a game). The title
 * and intro show first and stay up; a second later four examples play one at a time, Next between
 * them: each text fades in, and half a second after it the dice appear one at a time (two players
 * side by side, no roll animation; the right side a beat after the left): a 1-die tie, a 2-die tie
 * on different faces, a 3-die win (once both sides are in, the winner's dice blink green and grow,
 * the loser's shrink). Example 4 keeps Example 3's dice; only its text changes, and the winner
 * lunges at the loser, which is knocked askew and fades. The players' names come in with Example 1's dice and stay until the lesson closes;
 * between examples only the text and dice fade. Next is never greyed out (mid-example it
 * moves straight on); on the last it closes the lesson.
 */
import { useEffect, useState } from 'react';
import { ROLL_DIE_MS, t } from '@kadice/shared';
import { anim } from '../speed';
import { tx, txList } from '../text';
import { Dice } from './Dice';
import { PName } from './Names';

type Rolls = readonly [readonly number[], readonly number[]];

/** Both players' rolls in Examples 1-3 (Example 4 reuses the last). */
const ROLLS: readonly Rolls[] = [
  [[4], [4]],
  [
    [2, 5],
    [3, 4],
  ],
  [
    [6, 2, 5],
    [1, 4, 3],
  ],
];
const TEXTS = ['warLesson.ex1', 'warLesson.ex2', 'warLesson.ex3', 'warLesson.ex4'] as const;
const LAST = TEXTS.length - 1;
/** The dice shown at each step: Example 4 (the result) is Example 3's. */
const panelOf = (step: number) => Math.min(step, ROLLS.length - 1);

/** Fade in/out of each example (matches `.war-ex` in the CSS). */
const FADE_MS = 300;
/** The title and intro show alone this long before the first example. */
const INTRO_MS = 1000;
/** An example's text shows this long before its dice (Example 4: before the loser fades). */
const TEXT_MS = 500;
/** The right player's name, dice, total and tag each come in this long after the left's. */
const SIDE_MS = 150;
/** How often the timeline is checked. */
const TICK_MS = 50;

const sum = (dice: readonly number[]) => dice.reduce((a, b) => a + b, 0);
const BOTH = [0, 1] as const;

/**
 * A step's timeline (ms from its start, the right side `side` later than the left): when the text
 * shows, when the names appear (Example 1), when the first die appears (the rest one per `dieMs`),
 * and when the right side's last die has appeared.
 */
function timeline(step: number) {
  const dieMs = anim(ROLL_DIE_MS);
  const side = anim(SIDE_MS);
  if (step === LAST) return { dieMs, side, shown: 0, names: 0, start: TEXT_MS, end: TEXT_MS + side };
  const shown = step === 0 ? INTRO_MS : 0;
  const names = shown + anim(FADE_MS) + TEXT_MS;
  // Example 1: the first dice follow the right name.
  const start = step === 0 ? names + 2 * side : names;
  return { dieMs, side, shown, names, start, end: start + (ROLLS[step]![0].length - 1) * dieMs + side };
}

/**
 * Both players' names and dice side by side, each in a fixed box wide enough for the most dice,
 * with totals. Per side: `named` its name shown, `faces` the dice as shown (0 = not yet), `landed`
 * its total shown, `marked` (Example 4) its Winner / Loser shown. `decided`: the left side won
 * (its dice blink green and grow, the right side's shrink). Hidden parts keep their place.
 */
function Matchup({
  names,
  rolls,
  named,
  faces,
  landed,
  marked,
  decided = false,
}: {
  names: [string, string];
  rolls: Rolls;
  named: readonly boolean[];
  faces: Rolls;
  landed: readonly boolean[];
  marked: readonly boolean[];
  decided?: boolean;
}) {
  const hidden = (shown: boolean | undefined) => (shown ? undefined : { visibility: 'hidden' as const });
  return (
    <div className="war-ex-row">
      {rolls.map((dice, i) => (
        <div key={i} className={`war-ex-side${marked[i] ? (i === 0 ? ' won' : ' lost') : ''}`}>
          <span className="war-ex-name" style={hidden(named[i])}>
            <PName>{names[i]}</PName>
          </span>
          <Dice
            values={faces[i]!}
            winning={decided && i === 0 ? dice.map((_, d) => d) : undefined}
            losing={decided && i === 1 ? dice.map((_, d) => d) : undefined}
            small
          />
          <span className="war-ex-total" style={hidden(landed[i])}>
            {t('warLesson.total', { n: sum(dice) })}
          </span>
          <span className="war-ex-tag" style={hidden(marked[i])}>
            {t(i === 0 ? 'warLesson.winner' : 'warLesson.loser')}
          </span>
        </div>
      ))}
    </div>
  );
}

/** An example's text (Example 4 with its last-card note). */
function ExText({ step }: { step: number }) {
  const key = TEXTS[step]!;
  return (
    <>
      <p>{t(key)}</p>
      {key === 'warLesson.ex4' && <p className="war-ex-note">{t('warLesson.ex4Note')}</p>}
    </>
  );
}

/** `names`: the players at war (the examples use the first two); `card`: the card they all flipped (null if unknown). */
export function WarLessonBox({ names, card, onNext }: { names: string[]; card: string | null; onNext: () => void }) {
  const list = txList(names.map((n, i) => <PName key={i}>{n}</PName>));
  const pair: [string, string] = [names[0] ?? t('player.someone'), names[1] ?? t('player.someone')];

  // Where the lesson is: the step, ms into it, and whether it is fading out.
  const [play, setPlay] = useState({ step: 0, elapsed: 0, leaving: false });
  const { step, elapsed, leaving } = play;
  const time = timeline(step);
  const waiting = elapsed < time.shown;
  // Per side (the right a beat after the left): the dice due are shown, the rest not yet (keeping
  // their place); the total once its last die is in. Example 4 shows Example 3's dice from the start.
  const rolls = ROLLS[panelOf(step)]!;
  const due = (ms: number, i: number) => elapsed >= ms + i * time.side;
  const named = BOTH.map((i) => step > 0 || due(time.names, i));
  const faces = (
    step === LAST ? rolls : rolls.map((dice, i) => dice.map((v, d) => (due(time.start + d * time.dieMs, i) ? v : 0)))
  ) as Rolls;
  const sideLanded = BOTH.map((i) => step === LAST || due(time.start + (rolls[i]!.length - 1) * time.dieMs, i));
  const marked = BOTH.map((i) => step === LAST && due(time.start, i));
  // Example 3 (and 4, its result): the win shows once both sides are in.
  const decided = panelOf(step) === ROLLS.length - 1 && sideLanded.every(Boolean);

  useEffect(() => {
    const started = performance.now();
    const { end } = timeline(step);
    const tick = () => {
      const ms = performance.now() - started;
      setPlay((p) => (p.step === step ? { ...p, elapsed: ms } : p));
      if (ms >= end) clearInterval(timer);
    };
    const timer = setInterval(tick, TICK_MS);
    return () => clearInterval(timer);
  }, [step]);

  // Next: the last step closes; Example 4 swaps only the text; the others fade out first.
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => setPlay((p) => ({ step: p.step + 1, elapsed: 0, leaving: false })), anim(FADE_MS));
    return () => clearTimeout(timer);
  }, [leaving]);
  // Never greyed out: Next mid-example moves straight on.
  const next = () => {
    if (leaving) return;
    if (step === LAST) onNext();
    else if (step + 1 === LAST) setPlay({ step: LAST, elapsed: 0, leaving: false });
    else setPlay((p) => ({ ...p, leaving: true }));
  };

  return (
    <div className="modal-backdrop">
      <div className="modal splash war-lesson" role="dialog" aria-modal="true">
        <h2>{t('warLesson.title')}</h2>
        <p className="war-lesson-intro">
          {tx('warLesson.body', { names: list, card: <strong>{card ?? t('war.aCard')}</strong> })}
        </p>
        {/* The stage keeps one height for every example (an invisible sizer: every text stacked,
            the tallest wins, plus the dice row), so the box and Next never move. Over it, the
            current text with the names and dice right under it. Between examples only the text
            and dice fade; the names stay. */}
        <div className={`war-ex-stage${waiting ? ' waiting' : ''}${leaving ? ' leaving' : ''}`}>
          <div className="war-ex-sizer" aria-hidden>
            <div className="war-ex-texts">
              {TEXTS.map((key, i) => (
                <div key={key} className="war-ex-text">
                  <ExText step={i} />
                </div>
              ))}
            </div>
            <Matchup names={pair} rolls={rolls} named={BOTH.map(() => true)} faces={rolls} landed={[]} marked={[]} />
          </div>
          <div className="war-ex-live">
            {/* Remounted when it shows, so it fades in (Example 1 waits hidden, keeping its room). */}
            <div key={`${step}${waiting}`} className="war-ex-text">
              <ExText step={step} />
            </div>
            <Matchup
              names={pair}
              rolls={rolls}
              named={named}
              faces={faces}
              landed={sideLanded}
              marked={marked}
              decided={decided}
            />
          </div>
        </div>
        <div className="row actions">
          <button className="primary intro-next" onClick={next}>
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
